import type { CedarModuleConfig, CedarModuleRoute, CedarTool } from './types';

const normalizePath = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const validateTool = (moduleKey: string, tool: CedarTool): void => {
  if (!tool.key?.trim()) {
    throw new Error(`Module "${moduleKey}" has a tool with an empty key.`);
  }

  if (!tool.name?.trim()) {
    throw new Error(`Module "${moduleKey}" tool "${tool.key}" must define a name.`);
  }

  if (!tool.path?.trim()) {
    throw new Error(`Module "${moduleKey}" tool "${tool.key}" must define a path.`);
  }
};

const validateUniquePaths = (moduleKey: string, tools: CedarTool[]): void => {
  const pathToOwner = new Map<string, string>();

  for (const tool of tools) {
    const allPaths = [tool.path, ...(tool.aliases ?? [])].map(normalizePath);
    for (const path of allPaths) {
      const owner = pathToOwner.get(path);
      if (owner) {
        throw new Error(`Module "${moduleKey}" has duplicate route path "${path}" in tools "${owner}" and "${tool.key}".`);
      }
      pathToOwner.set(path, tool.key);
    }
  }
};

const createRoutesFromTools = (tools: CedarTool[]): CedarModuleRoute[] =>
  tools
    .filter((tool) => tool.enabled !== false && !tool.shouldHide)
    .map((tool) => ({ ...tool.nav, href: normalizePath(tool.path) }));

export function createModuleConfig(config: CedarModuleConfig) {
  const { key, domain, tools = [], name = domain.title, routes, ...rest } = config;

  tools.forEach((tool) => validateTool(key, tool));
  validateUniquePaths(key, tools);
  const resolvedRoutes = routes ?? createRoutesFromTools(tools);

  return {
    key,
    name,
    domain,
    tools,
    routes: resolvedRoutes,
    ...rest,
  };
}
